import { type Dispatch, type ReactNode, type SetStateAction, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { Dialog, DialogActions, DialogContent, DialogHeader } from "~/common/mui/Dialog/Dialog";
import {
  type ConfiguredQuickEntryItem,
  type QuickEntryForm,
  type QuickEntryItemPC,
  type QuickEntryPCForm,
} from "../types/models";
import { hasProductConfigured } from "../utils/helpers";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import CenterContents from "~/common/components/CenterContents";
import { LinkButton } from "~/common/components/Link";
import useMediaQuery, { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Box, Button, Stack, Typography, styled } from "~/common/mui";
import { getEmptyRow } from "~/features/quickEntry/constants/constants";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { ActionIconButton } from "~/common/components/Button/ActionIconButton";
import { QuickEntryTableRow } from "~/features/quickEntry/v2/QuickEntryTableRow";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

type Fields = "quantity" | "requestedDeliveryDate" | "comment" | "priceReference";

interface Props {
  onClose: () => void;
  maxAmount?: number;
  maxAmountMessage?: ValueProvider<ReactNode>;
  validateRow?: (entry: ConfiguredQuickEntryItem, itemsPopup?: QuickEntryItemPC[]) => ReactNode[];

  onAdd: (form: QuickEntryPCForm, setIsSending: Dispatch<SetStateAction<boolean>>, onClose: () => void) => void;
  variant?: "pc" | "wishlist" | "b2c-cart";
  disableAdd?: boolean;
  initialEmptyRowAmount?: number;
  addButtonLabel: ReactNode;
}

const PC_HEADER_COLS = ["11%", "54.5%", "34.5%"] as const;
const WISHLIST_HEADER_COLS = ["11%", "41.3%", "37%", "10.7%"] as const;

const PC_RIGHT_COLS = ["61.2%", "38.8%"] as const;
const WISHLIST_RIGHT_COLS = ["44.9%", "43%", "12%"] as const;

const getHeaderColumns = (variant: Props["variant"]) => (variant === "pc" ? PC_HEADER_COLS : WISHLIST_HEADER_COLS);

const getRightSideColumns = (variant: Props["variant"]) => (variant === "pc" ? PC_RIGHT_COLS : WISHLIST_RIGHT_COLS);

export const QuickEntryPopup = ({
  onClose,
  maxAmount = 0,
  validateRow,
  maxAmountMessage,
  onAdd,
  disableAdd,
  initialEmptyRowAmount = 1,
  variant = "pc",
  addButtonLabel,
}: Props) => {
  const { t } = useTranslation();

  const isMinTablet = useIsMinTabletSize();
  const isPhone = !isMinTablet;
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const defaultValues = useState(() => {
    const form: QuickEntryForm = { items: [] };
    for (let i = 0; i < initialEmptyRowAmount; i++) {
      form.items.push(getEmptyRow());
    }
    return form;
  })[0];

  const formProps = useForm<QuickEntryForm>({
    mode: "onBlur",
    defaultValues: defaultValues,
    shouldUnregister: false,
  });
  const { fields, remove, append } = useFieldArray({ control: formProps.control, name: "items" });

  const [isSending, setSending] = useState(false);

  const formRef = useRef<HTMLButtonElement>(null);
  const onSubmitClick = () => {
    return formRef.current?.click();
  };

  const watchItemsArray = formProps.watch("items");
  const items = fields.map((field, index) => {
    return {
      ...field,
      ...watchItemsArray[index],
    };
  });

  const itemsWithConfiguredProduct: ConfiguredQuickEntryItem[] = items.filter(
    hasProductConfigured,
  ) as ConfiguredQuickEntryItem[];

  const containErrors = !!formProps.formState.errors.items;

  const isAddToCartDisabled =
    itemsWithConfiguredProduct.length === 0 ||
    containErrors ||
    disableAdd ||
    (validateRow && itemsWithConfiguredProduct.some((row) => validateRow(row, items).length > 0));

  const onRemoveAllItems = () => {
    formProps.setValue("items", [getEmptyRow()]);
  };

  const maxReached = maxAmount && fields.length >= maxAmount;

  const appendNextRow = () => {
    if (!maxReached) {
      append(getEmptyRow());
    }
  };

  const headerColumns = getHeaderColumns(variant);
  const rightSideColumns = getRightSideColumns(variant);
  const isWishlist = ["wishlist", "b2c-cart"].includes(variant);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={"desktop-l"}
      slotProps={{
        paper: {
          sx: {
            margin: { phone: 0, tablet: 4 },
            maxHeight: { phone: "100dvh", tablet: "90%" },
            height: { phone: "100dvh", tablet: "auto" },
            maxWidth: "1200px",
            width: { phone: "100%", tablet: "720px", "desktop-s": "856px", "desktop-m": "780px", "desktop-l": "880px" },
          },
        },
      }}
    >
      <StyledDialogHeader onClose={onClose}>
        <Typography variant={"headlineSmall"}>{t("modal.quickEntry.title")}</Typography>
      </StyledDialogHeader>

      <StyledDialogContent>
        <FormProvider {...formProps}>
          <form
            onSubmit={formProps.handleSubmit((form) => onAdd(form, setSending, onClose))}
            className={isSending ? "v-hidden" : undefined}
          >
            <button ref={formRef} type={"submit"} hidden />
            <TableContainer>
              {isMinTablet && (
                <TableHeader>
                  {!isPhone && (
                    <TableHeaderCol $width={headerColumns[0]}>{t("wishlistPage.table.header.number")}</TableHeaderCol>
                  )}
                  <TableHeaderCol $width={headerColumns[1]} $flex>
                    {t("bulkUpload.table.header.productId")}
                  </TableHeaderCol>
                  {isWishlist && (
                    <TableHeaderCol $width={headerColumns[2]}>
                      {t("cartPage.table.header.quantity.title")}
                    </TableHeaderCol>
                  )}
                  <TableHeaderCol $width={isWishlist ? headerColumns[3] : headerColumns[2]} $justifyEnd={!isPhone}>
                    <ActionIconButton
                      icon={<DeleteIcon />}
                      onIconClick={onRemoveAllItems}
                      data-test-id={"btn-quick-remove-all"}
                    />
                  </TableHeaderCol>
                </TableHeader>
              )}
              <TableBody>
                {items.map((item, index) => (
                  <QuickEntryTableRow
                    key={item.id}
                    item={item}
                    index={index}
                    onRemove={() => {
                      if (fields.length > 1) {
                        remove(index);
                      } else {
                        onRemoveAllItems();
                      }
                    }}
                    items={items}
                    validateRow={validateRow}
                    variant={variant}
                    rightSideColumns={rightSideColumns}
                  />
                ))}
              </TableBody>
              <AppendActionRow>
                {isMinTablet && <Box sx={{ width: "11%" }} />}
                {!maxReached ? (
                  <LinkButton
                    type={"button"}
                    onFocus={isDesktop ? appendNextRow : undefined}
                    onClick={!isDesktop ? appendNextRow : undefined}
                    data-test-id={"btn-quick-entry-add-new-product"}
                    iconBefore={<AddIcon />}
                  >
                    {t("modal.quickEntry.newRow")}
                  </LinkButton>
                ) : (
                  render(maxAmountMessage)
                )}
              </AppendActionRow>
            </TableContainer>
          </form>

          {isSending && (
            <SpinnerOverlay>
              <CircleSpinner />
            </SpinnerOverlay>
          )}
        </FormProvider>
      </StyledDialogContent>

      <DialogActions>
        <Stack direction="row" gap={1}>
          <Button variant={"text"} onClick={onClose} data-test-id={"btn-quick-entry-cancel"}>
            {t("cancel")}
          </Button>
          <Button onClick={onSubmitClick} disabled={isAddToCartDisabled} data-test-id={"btn-quick-entry-submit"}>
            {addButtonLabel}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

type ValueProvider<T> = T | (() => T);
function render(val: ValueProvider<ReactNode>) {
  return typeof val === "function" ? val() : val;
}

const StyledDialogHeader = styled(DialogHeader)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
  marginBottom: "0",
}));

const StyledDialogContent = styled(DialogContent)({
  paddingTop: "0",
  position: "relative",
});

const TableContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const TableHeader = styled("div")(({ theme }) => ({
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  height: 40,
  borderBottom: `3px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("phone")]: {
    display: "none",
  },
}));

const TableHeaderCol = styled("div")<{ $width?: string; $flex?: boolean; $justifyEnd?: boolean }>(
  ({ theme, $width, $flex, $justifyEnd }) => ({
    padding: `0 ${theme.spacing(2)}`,
    width: $flex ? undefined : $width,
    flex: $flex ? 1 : undefined,
    display: "flex",
    justifyContent: $justifyEnd ? "flex-end" : undefined,
  }),
);

const TableBody = styled("div")(({ theme }) => ({
  overflow: "auto",
  maxHeight: "60vh",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("phone")]: {
    overflow: "initial",
    maxHeight: "unset",
  },
}));

const AppendActionRow = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(4),
  display: "flex",
}));

const SpinnerOverlay = styled(CenterContents)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});
